/**
 * Shared branded email shell and utilities. Pure functions — no Firebase
 * dependencies. Used by Cloud Functions (server-side send) and the admin
 * app (client-side preview).
 */

// Google Fonts import — email clients that support web fonts (Apple Mail,
// iOS Mail, some Android) render Playfair Display; others fall back to
// Georgia/serif. Body uses Inter with system fallbacks.
const FONT_IMPORT = "@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Inter:wght@400;500;600&display=swap');";
export const DISPLAY_FONT = "'Playfair Display', Georgia, 'Times New Roman', serif";
export const BODY_FONT = "'Inter', Arial, Helvetica, sans-serif";

/**
 * Brand-level hero image. Static asset, served from the admin tenant's public
 * folder. Has "Flat Rock Global Events" baked into the image (Playfair text
 * + dark navy scrim over a mountain photograph) — sidesteps the email-client
 * unreliability of CSS-overlaid text. At handoff, the client provisions an
 * equivalent at their domain and this constant updates.
 */
export const EMAIL_ASSET_BASE_URL = 'https://event.flatrockglobal.com';
export const HERO_IMAGE_URL = `${EMAIL_ASSET_BASE_URL}/email-hero.jpg`;

/**
 * Store-button glyphs (white Apple / Google Play marks on transparent).
 * Served from the admin tenant's public folder like the hero. Production
 * senders swap these for `cid:` inline attachments (see functions
 * `emailAssets.ts`) so Outlook renders them without external-image prompts.
 */
export const STORE_ICON_APPLE_URL = `${EMAIL_ASSET_BASE_URL}/email-icon-apple.png`;
export const STORE_ICON_GOOGLE_PLAY_URL = `${EMAIL_ASSET_BASE_URL}/email-icon-google-play.png`;
export const STORE_ICON_APPLE_CID = 'store-apple@event';
export const STORE_ICON_GOOGLE_PLAY_CID = 'store-google-play@event';

/**
 * Content-ID and filename used when embedding the hero as an inline MIME
 * attachment instead of fetching the remote URL. Production senders rewrite
 * their HTML to `<img src="cid:hero@event">` and attach the image with this
 * cid to bypass Outlook's external-content blocking. Preview surfaces (which
 * render in browsers, not email clients) keep using the remote URL because
 * browsers don't resolve cid: URIs.
 */
export const HERO_IMAGE_CID = 'hero@event';
export const HERO_INLINE_FILENAME = 'hero.jpg';

/**
 * Monitored contact address printed in the email footer. This is the Flat
 * Rock shared mailbox the Graph transport sends from, so replies to any
 * event email and questions sent here land in the same inbox.
 */
export const EVENTS_CONTACT_EMAIL = 'events@flatrockglobal.com';

// Brand gradient — kept as a fallback header for emails that opt out of the
// hero image (e.g., the internal-ops support email).
const GRADIENT_START = '#21376C';
const GRADIENT_END = '#3B5398';

export interface EmailShellOptions {
  eventName: string;
  bodyHtml: string;
  /**
   * Override the default hero image URL, or pass `null` to fall back to the
   * gradient-only header. Production callables omit this and get the brand
   * default; the preview harness overrides for localhost. Support email
   * passes `null`.
   */
  heroImageUrl?: string | null;
  footerHtml?: string;
}

/**
 * "When & Where" card shared by the invitation, event-added, and agenda
 * emails. Date and venue lines match the 16px body copy; the label stays a
 * small caps caption.
 */
export function buildWhenWhereCardHtml(
  dateRange: string,
  eventLocation: string | null,
  marginBottom: number = 28,
): string {
  const locationLine = eventLocation
    ? `<p style="margin:0;color:#444;font-family:${BODY_FONT};font-size:16px;line-height:1.5;">${escapeHtml(eventLocation)}</p>`
    : '';
  return `
              <div style="background:#f7f7fa;border-left:3px solid #21376C;padding:16px 20px;margin:0 0 ${marginBottom}px;">
                <p style="margin:0 0 6px;color:#21376C;font-family:${BODY_FONT};font-size:14px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;">When &amp; Where</p>
                <p style="margin:0 0 4px;color:#333;font-family:${BODY_FONT};font-size:16px;line-height:1.5;">${escapeHtml(dateRange)}</p>
                ${locationLine}
              </div>`;
}

const LIGHT_SCHEME_RULES_TOKEN = '__EMAIL_LIGHT_SCHEME_RULES__';

const INLINE_COLOR_RE = /(?:^|;)\s*color:\s*(#[0-9a-fA-F]{3,6})\b/;
const INLINE_BACKGROUND_RE = /(?:^|;)\s*background(?:-color)?:\s*(#[0-9a-fA-F]{3,6})\b/;

/**
 * Hold the light design in dark-mode mail clients. These emails are light
 * only; inverted they read badly. Everything here is derived from the
 * inline styles the templates already carry, so the templates stay plain.
 *
 * - Apple Mail / iOS Mail honor the color-scheme meta + CSS in the head.
 * - Outlook.com / new Outlook desktop rewrite inline colors and stamp
 *   `data-ogsc` (text) / `data-ogsb` (background) on the element they
 *   changed. Only descendant selectors work (`[data-ogsb] .x`, never
 *   `.x[data-ogsb]`), so the outer table carries both a background and a
 *   text color: it gets stamped, and every element inside can be pinned
 *   by its `keep-light-*` class. Unverified on those surfaces.
 * - Outlook for iOS is a verified dead end (2026-09-08, real sends): it
 *   recolors at paint time and exempts only <img> elements. The meta tags,
 *   these pins, gradient backgrounds, embedded PNG backgrounds, and
 *   WebKit text fill color all changed nothing. It renders in Outlook's
 *   own dark theme; do not add more layers for it.
 * - The Gmail app inverts everything except background images and text
 *   painted through one. On phones (Gmail iOS) every background is
 *   restated as a flat one-color gradient image and every text color is
 *   painted through a gradient clipped to the letters. Gmail Android
 *   (`div > u + .body`) is handed back to its own dark mode: its handling
 *   of the clip trick is unverified and invisible text is worse than an
 *   inverted email. Gmail web keeps message bodies light by itself; the
 *   phone-width query keeps it out of these rules. Both Gmail selectors
 *   hang off `u + .body` (Gmail swaps the doctype for a `<u>` and keeps
 *   the body's class), so the body carries `class="body"`.
 *
 * Text that sits on an element with its own background (the buttons)
 * gets a `keep-light-fg-*` class instead of `keep-light-c-*`, so the clip
 * trick never eats a button fill; the button label is wrapped in a span
 * that carries the text color on its own.
 */
function lockLightScheme(html: string): string {
  const textColors = new Map<string, string>();
  const surfaceTextColors = new Map<string, string>();
  const backgrounds = new Map<string, string>();
  const tagged = html.replace(
    /<(a|p|span|div|td|table|body|h1|h2|h3)\b([^>]*?)\sstyle="([^"]*)"/g,
    (match: string, tag: string, attrs: string, style: string) => {
      const classes: string[] = [];
      const color = INLINE_COLOR_RE.exec(style)?.[1];
      const background = INLINE_BACKGROUND_RE.exec(style)?.[1];
      if (background) {
        const cls = `keep-light-bg-${background.slice(1).toLowerCase()}`;
        backgrounds.set(cls, background);
        classes.push(cls);
      }
      if (color) {
        const hex = color.slice(1).toLowerCase();
        if (background) {
          const cls = `keep-light-fg-${hex}`;
          surfaceTextColors.set(cls, color);
          classes.push(cls);
        } else {
          const cls = `keep-light-c-${hex}`;
          textColors.set(cls, color);
          classes.push(cls);
        }
      }
      if (classes.length === 0) return match;
      const existing = /\sclass="([^"]*)"/.exec(attrs);
      if (existing) {
        const merged = attrs.replace(existing[0], ` class="${existing[1]} ${classes.join(' ')}"`);
        return `<${tag}${merged} style="${style}"`;
      }
      return `<${tag}${attrs} class="${classes.join(' ')}" style="${style}"`;
    },
  );

  const outlook = [
    ...[...textColors, ...surfaceTextColors].map(
      ([cls, value]) => `[data-ogsc] .${cls}{color:${value} !important;}`,
    ),
    ...[...backgrounds].map(([cls, value]) => `[data-ogsb] .${cls}{background:${value} !important;}`),
  ];
  const gmailPhone = [
    ...[...backgrounds].map(
      ([cls, value]) =>
        `u + .body .${cls}{background-image:linear-gradient(${value},${value}) !important;}`,
    ),
    ...[...textColors].map(
      ([cls, value]) =>
        `u + .body .${cls}{background-image:linear-gradient(${value},${value}) !important;-webkit-background-clip:text !important;background-clip:text !important;color:transparent !important;}`,
    ),
  ];
  const gmailAndroidRestore = [
    ...[...backgrounds].map(([cls]) => `div > u + .body .${cls}{background-image:none !important;}`),
    ...[...textColors].map(
      ([cls, value]) =>
        `div > u + .body .${cls}{background-image:none !important;-webkit-background-clip:border-box !important;background-clip:border-box !important;color:${value} !important;}`,
    ),
  ];
  const rules = [
    ...outlook,
    '@media only screen and (max-width:480px){',
    ...gmailPhone,
    ...gmailAndroidRestore,
    '}',
  ].join('\n');
  return tagged.replace(LIGHT_SCHEME_RULES_TOKEN, rules);
}

/**
 * Shared responsive email shell. Default behavior renders the brand hero
 * image (Flat Rock Global Events) at the top, body content, and footer.
 *
 * `eventName` is no longer rendered in the header — every consuming template
 * already states the event name in body copy. The hero is brand identity;
 * the body carries the event-specific identity.
 */
export function buildEmailShell({
  eventName,
  bodyHtml,
  heroImageUrl,
  footerHtml,
}: EmailShellOptions): string {
  const heroUrl = heroImageUrl === undefined ? HERO_IMAGE_URL : heroImageUrl;

  const headerCell = heroUrl
    ? `<td style="padding:0;">
              <img src="${heroUrl}" alt="Flat Rock Global Events" width="680" style="display:block;width:100%;max-width:680px;height:auto;border:0;outline:none;text-decoration:none;">
            </td>`
    : `<td style="background:linear-gradient(to right, ${GRADIENT_START}, ${GRADIENT_END});padding:32px;text-align:center;">
              <h1 style="margin:0;font-family:${DISPLAY_FONT};font-size:28px;color:#ffffff;font-weight:400;letter-spacing:0;">${escapeHtml(eventName)}</h1>
            </td>`;

  const footer = footerHtml
    ?? `<table role="presentation" width="100%" cellpadding="0" cellspacing="0">
              <tr>
                <td style="text-align:center;padding:8px 0 12px;">
                  <p style="margin:0 0 8px;font-family:${DISPLAY_FONT};font-size:14px;color:#21376C;font-weight:400;letter-spacing:0.04em;">Flat Rock Global Events</p>
                  <p style="margin:0;font-family:${BODY_FONT};font-size:14px;color:#999;line-height:1.6;">
                    Questions can be sent to: <a href="mailto:${EVENTS_CONTACT_EMAIL}" style="color:#21376C;text-decoration:none;">${EVENTS_CONTACT_EMAIL}</a>
                  </p>
                </td>
              </tr>
            </table>`;

  const shell = `<!DOCTYPE html>
<html lang="en">
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<meta name="color-scheme" content="light"><meta name="supported-color-schemes" content="light">
<style>${FONT_IMPORT}
:root{color-scheme:light only;supported-color-schemes:light only;}
${LIGHT_SCHEME_RULES_TOKEN}</style></head>
<body class="body" style="margin:0;padding:0;background:#f5f5f5;font-family:${BODY_FONT};" bgcolor="#f5f5f5">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f5f5f5;color:#333333;" bgcolor="#f5f5f5">
    <tr>
      <td align="center" style="padding:32px 16px;background:#f5f5f5;" bgcolor="#f5f5f5">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:680px;background:#ffffff;border-radius:8px;overflow:hidden;" bgcolor="#ffffff">
          <!-- Header (hero image or gradient fallback) -->
          <tr>
            ${headerCell}
          </tr>
          <!-- Body -->
          <tr>
            <td style="padding:32px;background:#ffffff;" bgcolor="#ffffff">
              ${bodyHtml}
            </td>
          </tr>
          <!-- Footer -->
          <tr>
            <td style="padding:16px 32px;background:#fafafa;border-top:1px solid #eee;" bgcolor="#fafafa">
              ${footer}
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
  return lockLightScheme(shell);
}

export function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
